import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { Image } from '@codes/ui-kit/components/ui/image';
import { WorkspaceView } from '@lemoncloud/codes-goods-api';
import { WorkspaceSettingDialog } from '@web/app/features/home/components';
import { useProjectService, useWorkspaceService } from '@web/app/features/home/hooks';
import { ToggleOption } from '@web/app/features/styling/components/ToggleOption';
import {
    getProjectBadge,
    getWorkspaceBadge,
    useCreateProjectDialog,
    useCreateWorkspaceDialog,
    useMyRole,
} from '@web/app/shared';
import { useDialogStore, useSelectedStore } from '@web/app/shared/stores';
import { Check, ChevronsLeft, ChevronsUpDown, Menu, Plus } from 'lucide-react';

import { CodesLogoImage, LogoMiniDarkImage } from '@codes/assets';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
    Button,
    Sheet,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
    Skeleton,
} from '@codes/ui-kit';

import { ConfirmWorkspaceDialog } from '../../../confirm-workspace-dialog';
import { CreateProjectDialog } from '../../../create-project-dialog';
import { CreateWorkspaceDialogV2 } from '../../../create-workspace-dialog';
import { EnrollCardDialog } from '../../../enroll-card-dialog';
import { PricingPlansDialog } from '../../../pricing-plans-dialog';
import { ServiceDetailDialog } from '../../../service-detail-dialog';
import { WorkspacePendingDialog } from '../../../workspace-pending-dialog';
import { MenuItem, getLightMenuItems, getMenuItems } from '../../consts';
import { UserBadge } from '../user-badge';

export const Header = ({ headerRef }: { headerRef: React.RefObject<HTMLDivElement> }) => {
    const { workspace: myWorksace, project: myProject, clearSelection, setProject } = useSelectedStore();
    const isWorkspaceSelected = !!myWorksace;
    const { myWorkspacesQueryResult, workspaceFromServerQueryData, replaceWorkspace, workspaceFromServer } =
        useWorkspaceService();
    const { myProjectsQueryResult } = useProjectService();
    const { t } = useTranslation();
    const location = useLocation();

    const navigate = useNavigate();

    const handleAddedCompleteToCreateWorkspace = (workspace: WorkspaceView) => {
        openWorkspaceConfirmDialog();
    };

    const { openWorkspaceConfirmDialog } = useDialogStore();

    const {
        setWorkspaceDialog,
        handleClose: handleCloseToCreateWorkspaceDialog,
        handleComplete: handleCompleteToCreateWorkspace,
        newWorkspace,
    } = useCreateWorkspaceDialog({
        onComplete: handleAddedCompleteToCreateWorkspace,
    });

    const {
        setProjectDialog,
        handleClose: handleCloseToCreateProjectDialog,
        handleComplete: handleCompleteToCreateProject,
    } = useCreateProjectDialog({
        onComplete: newProject => {
            navigate(`/w/${workspaceFromServer?.id}/p/${newProject.id}`);
        },
    });

    const WorkspacesDropDownContent = useMemo(() => {
        // 초기 데이터가 없을 때만 스켈레톤 표시
        const isInitialLoading =
            // (myWorkspacesQueryResult.isFetching && !myWorkspacesQueryResult.data) ||
            workspaceFromServerQueryData.isFetching && !workspaceFromServer;

        if (isInitialLoading) {
            return <Skeleton className="h-6 w-36" />;
        }

        if (workspaceFromServerQueryData.error || !workspaceFromServer) {
            return (
                <div className="flex gap-x-2 items-center">
                    <div className="text-error text-sm">{t('workspace.error.fetchDetail')}</div>
                    <Button
                        className="text-sm p-2 h-auto"
                        onClick={() => workspaceFromServerQueryData?.refetch?.()}
                        disabled={workspaceFromServerQueryData?.isRefetching}
                    >
                        {t('workspace.error.retry')}
                    </Button>
                </div>
            );
        }

        const WorkspaceList = () => {
            const isLoading = myWorkspacesQueryResult.isFetching || myWorkspacesQueryResult.isRefetching;

            if (isLoading) {
                return (
                    <div className="max-h-48 flex-col flex gap-y-1 overflow-y-auto">
                        <Skeleton className="h-8" />
                        <Skeleton className="h-8" />
                        <Skeleton className="h-8" />
                        <Skeleton className="h-8" />
                        <Skeleton className="h-8" />
                    </div>
                );
            }

            if (myWorkspacesQueryResult.isError) {
                return (
                    <div className="flex flex-col gap-y-2 items-center">
                        <div className="text-error text-sm">{t('workspace.error.fetchList')}</div>
                        <Button
                            className="text-sm p-2 h-auto"
                            onClick={() => myWorkspacesQueryResult.refetch()}
                            disabled={myWorkspacesQueryResult?.isRefetching}
                        >
                            {t('workspace.error.retry')}
                        </Button>
                    </div>
                );
            }
            return (
                <div className="max-h-48 overflow-y-auto">
                    {myWorkspacesQueryResult.data?.data?.map(workspace => (
                        <DropdownMenuItem
                            key={workspace.id}
                            onClick={() => replaceWorkspace(workspace.id as string, workspace.siteId as string)}
                            className="cursor-pointer !rounded-lg py-[7px] px-2 text-[13px] text-gray-900 gap-[5px] hover:bg-gray-100 truncate"
                        >
                            <div className="w-4 h-4 flex items-center justify-center">
                                {workspace.id === workspaceFromServer.id && <Check className="w-4 h-4 text-gray-800" />}
                            </div>
                            <div className="flex-1 flex gap-x-2 items-center justify-between min-w-0">
                                <span className="truncate flex-1">{workspace.name}</span>
                                {getWorkspaceBadge(workspace.stereo, false, 'h-[18px] px-2 text-xs')}
                            </div>
                        </DropdownMenuItem>
                    ))}
                </div>
            );
        };

        return (
            <>
                <DropdownMenuTrigger asChild className="max-sm:max-w-[180px] gap-[10px]">
                    <button className="flex items-center gap-1 py-1 px-[6px] rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:bg-gray-100">
                        <span className="text-gray-900 font-medium truncate">{workspaceFromServer.name}</span>
                        <div className="max-xs:hidden">{getWorkspaceBadge(workspaceFromServer.stereo)}</div>
                        <ChevronsUpDown className="shrink-0 w-4 h-4 text-gray-900 ml-[2px]" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[260px] p-[6px] rounded-lg space-y-[2px]">
                    <WorkspaceList />
                    <DropdownMenuSeparator className="m-0" />
                    <DropdownMenuItem
                        className="cursor-pointer !rounded-lg py-[7px] px-2 text-[13px] text-sub hover:bg-gray-100"
                        onClick={() => {
                            clearSelection();
                            navigate('/w');
                        }}
                    >
                        {t('workspace.allWorkspace')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="m-0" />
                    <DropdownMenuItem
                        onClick={() => setWorkspaceDialog(true)}
                        className="cursor-pointer !rounded-lg py-[7px] px-2 text-[13px] font-normal text-sub hover:bg-gray-100"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        <span>{t('workspace.list.newWorkspace')}</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
                <CreateProjectDialog
                    handleClose={handleCloseToCreateProjectDialog}
                    handleComplete={handleCompleteToCreateProject}
                    workspace={workspaceFromServer as WorkspaceView}
                />
            </>
        );
    }, [
        myWorkspacesQueryResult.data,
        myWorkspacesQueryResult.isFetching,
        myWorkspacesQueryResult.error,
        workspaceFromServerQueryData.isFetching,
        workspaceFromServerQueryData.error,
        workspaceFromServer,
        t,
        replaceWorkspace,
        clearSelection,
        navigate,
        setWorkspaceDialog,
        handleCloseToCreateProjectDialog,
        handleCompleteToCreateProject,
    ]);

    const ProjectsDropDownContent = () => {
        if (!myProject) {
            return null;
        }

        if (myProjectsQueryResult.isFetching || myProjectsQueryResult.isRefetching) {
            return <Skeleton className="h-6 w-36" />;
        }

        if (myProjectsQueryResult.error) {
            return (
                <div className="flex gap-x-2 items-center">
                    <div className="text-error">{t('project.error.fetchList')}</div>
                    <Button
                        className="text-sm p-2 h-auto"
                        onClick={() => myProjectsQueryResult.refetch()}
                        disabled={myProjectsQueryResult.isRefetching}
                    >
                        {t('project.error.retry')}
                    </Button>
                </div>
            );
        }

        return (
            <>
                <span className="text-tab-disabled mx-4 max-md:mx-0">/</span>
                <DropdownMenuTrigger
                    asChild
                    className="max-sm:max-w-[180px] gap-[10px]"
                    disabled={myProjectsQueryResult.isFetching}
                >
                    <button className="flex items-center gap-1 py-1 px-[6px] !rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:bg-gray-100">
                        <span className="text-gray-900 font-medium truncate">{myProject.name}</span>
                        <div className="max-xs:hidden">{getProjectBadge(myProject.stereo)}</div>
                        <ChevronsUpDown className="shrink-0 w-4 h-4 text-gray-900 ml-[2px]" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[260px] p-[6px] rounded-lg space-y-[2px]">
                    <div className="max-h-48 overflow-y-auto">
                        {myProjectsQueryResult.data?.data?.map(project => (
                            <DropdownMenuItem
                                key={project.id}
                                onClick={() => {
                                    setProject(project);
                                    navigate(`/w/${workspaceFromServer?.id}/p/${project.id}`);
                                }}
                                className="cursor-pointer !rounded-lg py-[7px] px-2 text-[13px] text-gray-900 gap-[5px] hover:bg-gray-100 truncate"
                            >
                                <div className="w-4 h-4 flex items-center justify-center">
                                    {project.id === myProject.id && <Check className="w-4 h-4 text-gray-800" />}
                                </div>
                                <div className="flex-1 flex gap-x-2 items-center justify-between min-w-0">
                                    <span className="truncate flex-1">{project.name}</span>
                                    {getProjectBadge(project.stereo, false, 'h-[18px] px-2 text-xs')}
                                </div>
                            </DropdownMenuItem>
                        ))}
                    </div>

                    <DropdownMenuSeparator className="m-0" />
                    <DropdownMenuItem
                        className="cursor-pointer !rounded-lg py-[7px] px-2 text-[13px] text-sub hover:bg-gray-100"
                        onClick={() => {
                            setProject(null);
                            if (workspaceFromServer) {
                                navigate(`/w/${workspaceFromServer.id}`, { replace: true });
                            } else {
                                navigate(`/w`, { replace: true });
                            }
                        }}
                    >
                        {t('project.allProject')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="m-0" />
                    <DropdownMenuItem
                        onClick={() => setProjectDialog(true)}
                        className="cursor-pointer !rounded-lg py-[7px] px-2 text-[13px] font-normal text-sub hover:bg-gray-100"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        <span>{t('project.create.createProject')}</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </>
        );
    };

    const myRole = useMyRole();

    const items = useMemo(() => {
        const menuItems = !myProject
            ? getLightMenuItems(workspaceFromServer?.id)
            : getMenuItems(workspaceFromServer?.id, myProject?.id);

        if (myRole > 1) {
            return menuItems.filter(item => !item.label.includes('settings'));
        }

        return menuItems;
    }, [workspaceFromServer?.id, myProject, myRole]);

    const [currentItem, setCurrentItem] = useState<string>('');

    const handleAccordionTriggerClick = (itemId: number) => {
        console.log(itemId, 'itemId');
        if (!itemId) {
            return;
        }

        const isClicked = `item-${itemId}` === currentItem;

        if (!isClicked) {
            setCurrentItem(`item-${itemId}`);

            return;
        }
        setCurrentItem('');
    };
    function isCurrentPath(url: string) {
        if (!url) {
            return false;
        }

        return location.pathname.includes(url);
    }

    const isAnySubPathCurrent = (item: MenuItem) => {
        if (item.href && isCurrentPath(item.href)) {
            return true;
        }
        if (item.subItems) {
            return item.subItems.some(sub => isCurrentPath(sub.href));
        }
        return false;
    };

    const navigateButtonTapped = (hasDropdown?: boolean, url?: string) => {
        if (hasDropdown) {
            return;
        }

        if (!url) {
            return;
        }
        navigate(url);
    };

    const SideBarSheet = () => {
        return (
            <Sheet>
                <SheetTrigger asChild>
                    <Button variant="ghost" className="p-1 h-auto">
                        <Menu size={16} />
                    </Button>
                </SheetTrigger>
                <SheetContent side="left" className="md:hidden py-[18px] px-[14px] w-[170px] dark:bg-[#02060E]">
                    <SheetHeader className="pt-0 h-10 border-none">
                        <SheetTitle></SheetTitle>
                        <SheetClose asChild>
                            <Button variant="ghost" className="p-1 h-auto">
                                <ChevronsLeft size={18} />
                            </Button>
                        </SheetClose>
                    </SheetHeader>
                    <Accordion type="single" collapsible className="w-full flex flex-col space-y-2">
                        {items.map((item, index) => (
                            <AccordionItem key={index} value={`item-${index}`}>
                                <AccordionTrigger
                                    className={`flex justify-between !p-2 text-sm  rounded-md ${
                                        item.hasDropdown ? '' : '[&>svg:last-child]:hidden'
                                    } ${
                                        isAnySubPathCurrent(item) ? 'bg-[#FFF9F4] dark:bg-[#301905]' : ''
                                    } data-[state=open]:!rounded-b-none`}
                                    onClick={() => navigateButtonTapped(item.hasDropdown, item.href)}
                                >
                                    <div className="flex items-center gap-2">
                                        {item.icon && <item.icon className="w-5 h-5 !rotate-0" />}
                                        <span>{t(item.label)}</span>
                                    </div>
                                </AccordionTrigger>
                                {item.hasDropdown && (
                                    <AccordionContent className="!py-2 bg-[#FFF9F4] dark:bg-[#301905] rounded-b-md">
                                        {item.subItems?.map((subItem, subIndex) => (
                                            <button
                                                onClick={() => navigateButtonTapped(false, subItem.href)}
                                                key={subIndex}
                                                className={`w-full text-left px-3 py-[5px] rounded-sm text-[15px] font-medium active:text-point ${
                                                    isAnySubPathCurrent(subItem) ? 'text-point' : ''
                                                }`}
                                            >
                                                {t(subItem.label)}
                                            </button>
                                        ))}
                                    </AccordionContent>
                                )}
                            </AccordionItem>
                        ))}
                    </Accordion>
                </SheetContent>
            </Sheet>
        );
    };

    if (!isWorkspaceSelected) {
        return (
            <header
                ref={headerRef}
                className="fixed left-0 md:left-[82px] right-0 z-[99] flex flex-col md:pt-6 max-md:shadow-custom"
            >
                <div className="flex flex-1 items-center justify-between pr-20 max-md:px-0 max-md:bg-background ">
                    <div className="h-[51px] flex items-center gap-[7px] bg-background md:rounded-full py-3 px-4 md:border-[0.6px] md:border-gray-200 md:shadow-custom">
                        <a href="/">
                            <Image src={CodesLogoImage} alt="Eureka Codes" className="h-[26px]" />
                        </a>
                    </div>
                    <div className="flex items-center md:gap-[18px]">
                        <ToggleOption />
                        <UserBadge />
                    </div>
                </div>
                <PricingPlansDialog />
                <CreateWorkspaceDialogV2
                    handleClose={handleCloseToCreateWorkspaceDialog}
                    handleComplete={handleCompleteToCreateWorkspace}
                />
                <ConfirmWorkspaceDialog workspace={newWorkspace} />
                <EnrollCardDialog />
            </header>
        );
    }

    return (
        <header
            ref={headerRef}
            className="fixed left-0 md:left-[82px] right-0 z-[99] flex flex-col md:pt-6 max-md:h-auto max-md:shadow-custom"
        >
            <div className="flex flex-1 items-center justify-between max-md:px-0 max-md:bg-background px-[42px]">
                <div className="h-[51px] flex items-center gap-[7px] md:bg-white md:dark:bg-[#424242] md:rounded-full py-3 px-4 md:border-[0.6px] md:border-gray-200 md:shadow-custom">
                    <button
                        className="mr-[7px] md:hidden"
                        onClick={() => navigate(workspaceFromServer?.id ? `/w/${workspaceFromServer.id}` : '/w')}
                    >
                        <Image className="w-[26px] h-[26px]" src={LogoMiniDarkImage} />
                    </button>
                    <div className="flex items-center max-md:hidden">
                        <DropdownMenu>{WorkspacesDropDownContent}</DropdownMenu>
                        <DropdownMenu>
                            <ProjectsDropDownContent />
                        </DropdownMenu>
                    </div>
                </div>
                <div className="flex items-center md:gap-[18px]">
                    <ToggleOption />
                    <UserBadge />
                </div>
            </div>
            {workspaceFromServer && (
                <div className="bg-background max-md:flex flex-1 hidden border-t border-detail-line max-md:px-5 max-md:py-1 gap-x-2 items-center overflow-auto">
                    <SideBarSheet />
                    <DropdownMenu>{WorkspacesDropDownContent}</DropdownMenu>

                    <DropdownMenu>
                        <ProjectsDropDownContent />
                    </DropdownMenu>
                </div>
            )}

            <CreateWorkspaceDialogV2
                handleClose={handleCloseToCreateWorkspaceDialog}
                handleComplete={handleCompleteToCreateWorkspace}
            />
            <ConfirmWorkspaceDialog workspace={newWorkspace} />

            <PricingPlansDialog />
            <EnrollCardDialog />

            <WorkspacePendingDialog />

            <ServiceDetailDialog />
            <WorkspaceSettingDialog />
        </header>
    );
};
